/* eslint-disable react/jsx-props-no-spreading */
import React, { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  Dialog,
  Select,
  Menu,
  Warning as WarningIcon
  // @ts-ignore
} from '@netbasequid/core'
import styled from 'styled-components'
import { useForm, Controller, FormProvider, useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useDisplayTheme, useSysTheme } from 'hooks/useUiState'
import { SystemTheme } from 'hooks/useUiState/types'
import { closeScheduledReportConfigModal } from 'features/scheduledReport/scheduledReportSlice'
import { selectIsScheduledReportConfigModalOpen } from 'features/scheduledReport/selectors'
import { ScheduledReport } from 'features/scheduledReport/types'
import { selectUserTimeZone } from 'features/config/selectors'
import { selectHasLegacyWidgets } from 'features/dashboard/selectors'
import {
  createUpdateScheduledReport,
  deleteScheduledReport
} from 'features/scheduledReport/asyncThunks'
import AlertFrequency from 'enums/AlertFrequency'
import SavingButton from 'components/visualization/SavingButton'
import { AutomationId, setAutoId } from 'utils/automationUtil'

import ErrorDialog from './ErrorDialog'
import RadioWithLabel from './RadioWithLabel'
import Subscribers from './Subscribers'
import { dayOfWeekMap, dayOfMonth, hours, frequency, FREQUENCY_OFF, getCleanedData } from './utils'
import { useRetrieveScheduledReportInfos } from './hooks'

const StyledDialogContent = styled(Dialog.DialogContent)`
  opacity: ${({ disable }) => (disable ? 0.4 : 1)};
  pointer-events: ${({ disable }) => (disable ? 'none' : 'auto')};
`

const StyledDialogContentInner = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 16px 12px;
  padding-top: 8px;
  & > :nth-child(odd) {
    padding-top: 6px;
  }
`
const WarningContainer = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 16px;
`
const PlaceholderContainer = styled.div<{ $theme: SystemTheme; $size?: string }>`
  color: ${({ $theme }) => $theme.text.placeholder};
  font-size: ${({ $theme, $size }) => $theme.typescale[$size === 'small' ? 12 : 14].size};
  font-weight: ${({ $theme }) => $theme.fontWeight.regular};
  line-height: ${({ $theme, $size }) => $theme.typescale[$size === 'small' ? 12 : 14].lineHeight};
`

const RowContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const RadioContainer = styled.div`
  height: 34px;
  display: flex;
  align-items: center;
`

const FormRow = ({
  field,
  children,
  placeholder,
  labelTestId,
  contentTestId,
  visible = true
}: {
  field: React.ReactNode
  children: React.ReactNode
  placeholder?: React.ReactNode
  labelTestId?: string
  contentTestId?: string
  visible?: boolean
}) => {
  const theme = useSysTheme()
  if (!visible) return null
  return (
    <>
      <div {...(labelTestId && setAutoId(labelTestId))}>{field}</div>
      <div {...(contentTestId && setAutoId(contentTestId))}>
        {placeholder ? (
          <RowContainer>
            {children}
            <PlaceholderContainer $theme={theme}>{placeholder}</PlaceholderContainer>
          </RowContainer>
        ) : (
          children
        )}
      </div>
    </>
  )
}

const ControlledSelect = ({
  children,
  name,
  width,
  menuProps
}: {
  children: React.ReactNode
  name: string
  width: string
  menuProps?: Record<string, unknown>
}) => {
  const { control } = useFormContext()
  const mode = useDisplayTheme()
  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: true }}
      render={({ field }) => {
        const { ref, ...restField } = field
        return (
          <Select width={width} mode={mode} menuProps={menuProps} {...restField}>
            {children}
          </Select>
        )
      }}
    />
  )
}

const ScheduledReportConfigModal = () => {
  const {
    defaultScheduledReportInfo,
    defaultSubscribers,
    isLoading,
    isSaving
  } = useRetrieveScheduledReportInfos()
  const mode = useDisplayTheme()
  const theme = useSysTheme()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { dashboardId } = useParams()
  const userTimeZone = useAppSelector(selectUserTimeZone)
  const hasLegacyWidgets = useAppSelector(selectHasLegacyWidgets)
  const methods = useForm({
    defaultValues: defaultScheduledReportInfo
  })
  const { register, handleSubmit, watch } = methods

  const currentFrequency = watch('frequency')
  const isFrequencyOff = currentFrequency === FREQUENCY_OFF
  const isOpen = useAppSelector(selectIsScheduledReportConfigModalOpen)

  const onClose = () => {
    dispatch(closeScheduledReportConfigModal())
  }

  const handleSave = (data: ScheduledReport) => {
    if (isFrequencyOff) {
      dispatch(deleteScheduledReport(Number(dashboardId)))
    } else {
      dispatch(
        createUpdateScheduledReport({ id: Number(dashboardId), reportConfig: getCleanedData(data) })
      )
    }
  }

  return (
    <>
      <Dialog
        mode={mode}
        open={isOpen}
        onClose={onClose}
        width="760px"
        maxHeight="85vh"
        isStretchedDivider>
        <Dialog.DialogTitle>{t('scheduledReport')}</Dialog.DialogTitle>
        <StyledDialogContent disable={isSaving}>
          {/* @ts-ignore */}
          <FormProvider {...methods}>
            <StyledDialogContentInner>
              <FormRow
                field={t('generateReport')}
                labelTestId={AutomationId.scheduledReport.modal.label.frequency}
                contentTestId={AutomationId.scheduledReport.modal.content.frequency}>
                <ControlledSelect name="frequency" width="210px">
                  {Object.entries(frequency).map(([key, value]) => (
                    <Menu.MenuItem value={key} key={key}>
                      {t(value)}
                    </Menu.MenuItem>
                  ))}
                </ControlledSelect>
              </FormRow>

              <FormRow
                field={t('mode')}
                visible={!isFrequencyOff}
                labelTestId={AutomationId.scheduledReport.modal.label.mode}
                contentTestId={AutomationId.scheduledReport.modal.content.mode}>
                <RadioContainer>
                  <RadioWithLabel {...register('colorMode')} value="LIGHT" label={t('lightMode')} />
                  <RadioWithLabel {...register('colorMode')} value="DARK" label={t('darkMode')} />
                </RadioContainer>
              </FormRow>
              <FormRow
                field={t('at')}
                placeholder={userTimeZone}
                visible={!isFrequencyOff}
                labelTestId={AutomationId.scheduledReport.modal.label.at}
                contentTestId={AutomationId.scheduledReport.modal.content.at}>
                <ControlledSelect
                  name="alertHourFirst"
                  width="120px"
                  menuProps={{ maxHeight: '222px' }}>
                  {hours.map((hour) => (
                    <Menu.MenuItem value={hour} key={hour}>
                      {hour}
                    </Menu.MenuItem>
                  ))}
                </ControlledSelect>
              </FormRow>

              <FormRow
                field={t('And')}
                placeholder={userTimeZone}
                visible={currentFrequency === AlertFrequency.TWICE_DAILY}
                labelTestId={AutomationId.scheduledReport.modal.label.and}
                contentTestId={AutomationId.scheduledReport.modal.content.and}>
                <ControlledSelect
                  name="alertHourSecond"
                  width="120px"
                  menuProps={{ maxHeight: '222px' }}>
                  {hours.map((hour) => (
                    <Menu.MenuItem value={hour} key={hour}>
                      {hour}
                    </Menu.MenuItem>
                  ))}
                </ControlledSelect>
              </FormRow>

              <FormRow
                field={t('every')}
                visible={currentFrequency === AlertFrequency.WEEKLY}
                labelTestId={AutomationId.scheduledReport.modal.label.every}
                contentTestId={AutomationId.scheduledReport.modal.content.every}>
                <ControlledSelect name="dayOfWeek" width="120px">
                  {Object.entries(dayOfWeekMap).map(([key, value]) => (
                    <Menu.MenuItem value={key} key={key}>
                      {t(value)}
                    </Menu.MenuItem>
                  ))}
                </ControlledSelect>
              </FormRow>

              <FormRow
                field={t('every')}
                placeholder={t('dayOfMonth')}
                visible={currentFrequency === AlertFrequency.MONTHLY}
                labelTestId={AutomationId.scheduledReport.modal.label.every}
                contentTestId={AutomationId.scheduledReport.modal.content.every}>
                <ControlledSelect name="dayOfMonth" width="120px">
                  {Object.entries(dayOfMonth).map(([key, value]) => (
                    <Menu.MenuItem value={key} key={key}>
                      {t(value)}
                    </Menu.MenuItem>
                  ))}
                </ControlledSelect>
              </FormRow>

              <FormRow
                field={null}
                visible={!isFrequencyOff}
                contentTestId={AutomationId.scheduledReport.modal.content.delayWarning}>
                <PlaceholderContainer $theme={theme}>
                  {t('scheduledReportDelayReminder')}
                </PlaceholderContainer>
              </FormRow>

              <FormRow
                field={t('subscriber_other')}
                visible={!isFrequencyOff}
                labelTestId={AutomationId.scheduledReport.modal.label.subscribers}
                contentTestId={AutomationId.scheduledReport.modal.content.subscribers}>
                <Subscribers defaultSubscribers={defaultSubscribers} isLoading={isLoading} />
              </FormRow>
            </StyledDialogContentInner>
          </FormProvider>
          {!isFrequencyOff && hasLegacyWidgets && (
            <WarningContainer>
              <div>
                <WarningIcon fontSize={16} color={theme.semantic.middle.default} />
              </div>
              <PlaceholderContainer
                $theme={theme}
                $size="small"
                {...setAutoId(AutomationId.scheduledReport.modal.limitedDashboardReminder)}>
                {t('scheduledReportLimitedDashboardReminder')}
              </PlaceholderContainer>
            </WarningContainer>
          )}
        </StyledDialogContent>

        <Dialog.DialogActions
          closeButtonProps={{
            children: t('cancel'),
            onClick: onClose,
            disabled: isSaving
          }}
          okButtonProps={{
            children: isSaving ? <SavingButton /> : t('save'),
            onClick: handleSubmit(handleSave),
            disabled: isSaving || isLoading
          }}
        />
      </Dialog>
      <ErrorDialog />
    </>
  )
}

export default memo(ScheduledReportConfigModal)
